import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LoadingPage } from "@/components/ui/loading-spinner";
import { EmptyState } from "@/components/ui/empty-state";
import { GraduationCap, Search, Calendar, DollarSign, ExternalLink, Award, CheckCircle2 } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const Scholarships = () => {
  const { token, user } = useContext(AuthContext);
  const { t } = useLanguage();
  const [scholarships, setScholarships] = useState([]);
  const [filteredScholarships, setFilteredScholarships] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");

  useEffect(() => {
    fetchScholarships();
  }, []);

  useEffect(() => {
    filterScholarships();
  }, [searchTerm, selectedCategory, scholarships]);

  const fetchScholarships = async () => {
    try {
      const gradeParam = user?.grade ? `?grade=${user.grade}` : '';
      const response = await axios.get(`${API}/scholarships${gradeParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setScholarships(response.data);
      setFilteredScholarships(response.data);
    } catch (error) {
      toast.error("Failed to load scholarships");
    } finally {
      setLoading(false);
    }
  };

  const filterScholarships = () => {
    let filtered = scholarships;

    if (searchTerm) {
      filtered = filtered.filter(
        (scholarship) =>
          scholarship.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          scholarship.organization.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCategory !== "All") {
      filtered = filtered.filter((s) => s.category === selectedCategory);
    }

    setFilteredScholarships(filtered);
  };

  const categories = ["All", ...new Set(scholarships.map((s) => s.category))];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <Navbar />
        <LoadingPage message="Loading scholarships..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-8" data-testid="scholarships-container">
        {/* Compact Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-success to-success/80 flex items-center justify-center shadow-md">
              <Award className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                {t('scholarships.title')}
              </h1>
              <p className="text-gray-600 text-sm md:text-base">
                {t('scholarships.subtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <Card className="mb-8 border-none shadow-lg">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
                <Input
                  type="text"
                  placeholder={t('scholarships.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 h-12 text-base border-none bg-muted/30"
                  data-testid="scholarship-search-input"
                />
              </div>
            </div>
            <div className="flex gap-2 flex-wrap mt-4">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                  className={selectedCategory === category ? "shadow-md" : ""}
                  data-testid={`category-${category.toLowerCase()}`}
                >
                  {category === "All" ? t('scholarships.allCategories') : category}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Results Count */}
        {filteredScholarships.length > 0 && (
          <div className="mb-6">
            <p className="text-muted-foreground">
              Showing <span className="font-semibold text-foreground">{filteredScholarships.length}</span> scholarship{filteredScholarships.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}

        {/* Scholarships Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {filteredScholarships.map((scholarship) => (
            <Card key={scholarship.id} className="group border-none shadow-md hover:shadow-2xl cursor-pointer transition-all duration-300 overflow-hidden" data-testid={`scholarship-card-${scholarship.id}`}>
              {/* Gradient Top Bar */}
              <div className="h-2 bg-gradient-to-r from-success via-success/80 to-success/60"></div>
              
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between mb-3">
                  <Badge variant="outline" className="bg-success/10 text-success border-success/20 text-xs font-medium">
                    {scholarship.category}
                  </Badge>
                </div>
                <CardTitle className="text-xl mb-2 group-hover:text-primary transition-colors">
                  {scholarship.name}
                </CardTitle>
                <CardDescription className="text-sm leading-relaxed">
                  {scholarship.organization}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-3 pt-0">
                <div className="space-y-2">
                  <div className="flex items-center gap-3 p-3 bg-success/5 rounded-lg">
                    <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
                      <DollarSign className="w-5 h-5 text-success" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Amount</p>
                      <p className="font-bold text-success text-lg">{scholarship.amount}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 text-sm text-muted-foreground p-3 bg-muted/30 rounded-lg">
                    <div className="w-8 h-8 rounded-lg bg-warning/10 flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-4 h-4 text-warning" />
                    </div>
                    <div>
                      <p className="text-xs">Deadline</p>
                      <p className="font-medium text-foreground">{new Date(scholarship.deadline).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
                
                <div className="pt-2">
                  <p className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    {t('scholarships.eligibility')}:
                  </p>
                  <ul className="space-y-2">
                    {scholarship.eligibility.slice(0, 2).map((req, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <span className="w-1.5 h-1.5 bg-primary rounded-full mt-1.5 flex-shrink-0"></span>
                        <span className="line-clamp-1">{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <Button className="w-full mt-4 shadow-md group-hover:shadow-lg transition-shadow" size="sm" asChild>
                  <a href={scholarship.application_link} target="_blank" rel="noopener noreferrer">
                    {t('scholarships.applyNow')} <ExternalLink className="w-4 h-4 ml-2" />
                  </a>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {filteredScholarships.length === 0 && (
          <EmptyState
            icon={GraduationCap}
            title={t('scholarships.noResults')}
            description="Try adjusting your search or filters to find scholarships."
            data-testid="no-scholarships-message"
          />
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Scholarships;